import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  IconButton,
  Collapse,
  Button,
  alpha,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme/tokens';

/**
 * FilterBar Component
 * 
 * A search and filter bar with debounced search input and optional collapsible filters.
 * Includes mobile-responsive design with a drawer for filters on small screens.
 * Follows WCAG 2.1 AA guidelines with proper keyboard navigation and ARIA attributes.
 * 
 * @component
 * @example
 * ```jsx
 * <FilterBar
 *   value={searchTerm}
 *   onChange={handleSearchChange}
 *   onClear={handleClearFilters}
 *   placeholder="Search users..."
 * >
 *   <Select label="Role" value={role} onChange={handleRoleChange}>
 *     <MenuItem value="all">All Roles</MenuItem>
 *     <MenuItem value="physician">Physician</MenuItem>
 *   </Select>
 * </FilterBar>
 * ```
 */
const FilterBar = ({
  value,
  onChange,
  onClear,
  children,
  placeholder = 'Search...',
  debounceMs = 300,
  showClearButton = true,
  ariaLabel = 'search and filter controls',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [internalValue, setInternalValue] = useState(value || '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debounceTimeout = useRef(null);

  // Sync internal value with prop value
  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);

  // Debounced onChange handler
  const handleSearchChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      setInternalValue(newValue);

      // Clear existing timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      // Set new timeout
      debounceTimeout.current = setTimeout(() => {
        onChange(newValue);
      }, debounceMs);
    },
    [onChange, debounceMs]
  );

  // Clear all filters
  const handleClearAll = useCallback(() => {
    setInternalValue('');
    onChange('');
    if (onClear) {
      onClear();
    }
  }, [onChange, onClear]);

  // Toggle filters
  const handleToggleFilters = useCallback(() => {
    setFiltersOpen((prev) => !prev);
  }, []);

  // Close filters (for mobile drawer)
  const handleCloseFilters = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const hasActiveFilters = value || (children && filtersOpen);

  return (
    <Box
      sx={{
        width: '100%',
        mb: 1,
      }}
      role="search"
      aria-label={ariaLabel}
    >
      {/* Search Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          backgroundColor: theme.palette.background.paper,
          borderRadius: `${tokens.radius.md}px`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: tokens.shadow.sm,
          transition: `box-shadow ${tokens.transition.base}`,
          '&:focus-within': {
            boxShadow: tokens.shadow.md,
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
      >
        <SearchIcon
          sx={{
            color: theme.palette.text.secondary,
            fontSize: 20,
          }}
          aria-hidden="true"
        />

        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={internalValue}
          onChange={handleSearchChange}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            'aria-label': placeholder,
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: tokens.fontSize.sm,
              py: 0.5,
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
            },
          }}
        />

        {showClearButton && (internalValue || hasActiveFilters) && (
          <IconButton
            size="small"
            onClick={handleClearAll}
            aria-label="Clear all filters"
            sx={{
              minWidth: tokens.size.iconButton,
              minHeight: tokens.size.iconButton,
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}

        {children && (
          <IconButton
            size="small"
            onClick={handleToggleFilters}
            aria-label={filtersOpen ? 'Close filters' : 'Open filters'}
            aria-expanded={filtersOpen}
            aria-controls="filter-panel"
            sx={{
              minWidth: tokens.size.iconButton,
              minHeight: tokens.size.iconButton,
              color: filtersOpen ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Filters Panel - Desktop (Collapse) */}
      {children && !isMobile && (
        <Collapse
          in={filtersOpen}
          timeout="auto"
          unmountOnExit
          id="filter-panel"
        >
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: theme.palette.background.paper,
              borderRadius: `${tokens.radius.md}px`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: tokens.shadow.sm,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              {children}
            </Box>
          </Box>
        </Collapse>
      )}

      {/* Filters Panel - Mobile (Drawer) */}
      {children && isMobile && (
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={handleCloseFilters}
          PaperProps={{
            sx: {
              borderTopLeftRadius: tokens.radius.xl,
              borderTopRightRadius: tokens.radius.xl,
              maxHeight: '80vh',
            },
          }}
        >
          <Box
            sx={{
              p: tokens.space.x3,
            }}
            role="dialog"
            aria-label="Filter options"
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: tokens.space.x3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                Filters
              </Typography>
              <IconButton
                onClick={handleCloseFilters}
                aria-label="Close filters"
                sx={{
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: tokens.space.x3 }} />

            {/* Filter Controls */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space.x3,
              }}
            >
              {children}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: tokens.space.x2,
                mt: tokens.space.x4,
                pt: tokens.space.x3,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearAll}
                sx={{
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCloseFilters}
                sx={{
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

FilterBar.propTypes = {
  /** Current search value */
  value: PropTypes.string.isRequired,
  /** Callback when search value changes (debounced) */
  onChange: PropTypes.func.isRequired,
  /** Callback when clear button is clicked */
  onClear: PropTypes.func,
  /** Filter controls to display in collapsible panel */
  children: PropTypes.node,
  /** Placeholder text for search input */
  placeholder: PropTypes.string,
  /** Debounce delay in milliseconds */
  debounceMs: PropTypes.number,
  /** Whether to show the clear button */
  showClearButton: PropTypes.bool,
  /** Accessible label for the search region */
  ariaLabel: PropTypes.string,
};

export default FilterBar;
